import React, {useCallback, useEffect, useState} from 'react';

import {
    sendDesktopAnalyticsEvent,
    sendDesktopAnalyticsPerSession,
} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {getButton} from '@optimaxdev/design-system';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {useFeatureWithAnalytics} from 'components/feature';
import {CART_BUTTON} from 'constants/analytics/events/cart';
import {GENERAL_EXPERIMENT_INDICATION} from 'constants/analytics/events/general';
import {REFERER_FIND} from 'constants/analytics/mentionMe';
import {PayOverTimeLocations} from 'constants/payOverTime';
import {MentionMe} from 'desktop/components/mentionMe/mentionMe';
import {PayOverTime} from 'desktop/features/payOverTime/payOverTime';
import {useHasGlassesCare} from 'desktop/hooks/useHasGlassesCare/useHasGlassesCare';
import {getTotalRebateSum} from 'desktop/pages/checkout/store/selectors/orderReviewTotals/orderReviewTotals';
import {ApplicationStoreType} from 'desktop/store';
import {useHostSelector} from 'hooks/hostHooks/hostHooks';
import {flags} from 'libs/rollout';
import {getRouterSearchParams} from 'selectors/router';

import {
    getCartItemsCount,
    getHasOutOfStock,
    getIsCartHasActiveSubscriptionCL,
} from '../../store/selectors';
import {Advantages} from '../advantages/advantages';
import {CartSummaryButtons} from '../cartSummaryButtons/cartSummaryButtons';
import {CartSummaryGrandTotalConnected} from '../cartSummaryGrandTotal/cartSummaryGrandTotal';
import {CartSummaryList} from '../cartSummaryList/cartSummaryList';
import {useGetIsCartEligibleForNextDayShipping} from '../nextDayShippingBanner/utils/useGetIsCartEligibleForNextDayShipping';
import {NextDayShippingBlockInSummary} from '../nextDayShippingBlockInSummary/nextDayShippingBlockInSummary';

import {PropsType} from './types';

import s from './cartSummary.scss';

const sendAnalytic = () =>
    sendDesktopAnalyticsEvent(CART_BUTTON, {
        action: 'Right Section',
        label: 'Referral Program - Clicked',
    });
/**
 * Displays all prices, discounts for order in summery block
 * @returns {CartSummary} - component
 */
// eslint-disable-next-line max-statements
export const CartSummary: React.FC<PropsType> = ({
    activeItemsCount,
    copay,
    hasOutOfStock,
    isClRebate,
    isSubscriptionLenses,
    itemsCount,
    shippingProtection,
    subtotal,
    total,
    totalRebateDiscount,
}) => {
    const navigate = useNavigate();
    const [loadingButton, setLoadingButton] = useState(false);
    const {data: isCartEligibleForNextDayData} = useGetIsCartEligibleForNextDayShipping();
    const urlSearchParams = useHostSelector(getRouterSearchParams);
    const hasGlassesCare = useHasGlassesCare();
    const buttonProps = getButton({onClick: sendAnalytic, onKeyDown: sendAnalytic});
    const isDCartUICouponBox = useFeatureWithAnalytics('dCartUI_CouponBox', false, 'desktop');

    /**
     * Redirect to checkout page
     */
    const goToCheckout = useCallback(() => {
        setLoadingButton(true);
        navigate(`/checkout/onepage/${urlSearchParams || ''}`);
    }, [urlSearchParams, navigate]);

    const isClRebateEnabled = isClRebate && totalRebateDiscount;
    const dCartNextDayImp = flags.dCartNextDayImp.isEnabled();
    const isDisablePaymentButtons = isSubscriptionLenses || hasOutOfStock || hasGlassesCare;

    const storeNextDayShippingBlock = () =>
        isCartEligibleForNextDayData?.isCartEligibleForNextDayShipping &&
        dCartNextDayImp &&
        Boolean(activeItemsCount) && <NextDayShippingBlockInSummary />;

    useEffect(() => {
        if (isCartEligibleForNextDayData)
            sendDesktopAnalyticsPerSession({
                event: GENERAL_EXPERIMENT_INDICATION,
                isInteraction: false,
                label: {
                    expName: 'dCartNextDayImp',
                    label: dCartNextDayImp,
                },
            });
    }, [dCartNextDayImp, isCartEligibleForNextDayData]);

    return (
        <div
            className={classNames(s.container, {
                [s.emptyContainer]: itemsCount === 0,
            })}
        >
            <CartSummaryList
                activeItemsCount={activeItemsCount}
                copay={copay}
                shippingProtection={shippingProtection}
                subtotal={subtotal}
            />
            {storeNextDayShippingBlock()}
            <CartSummaryGrandTotalConnected />

            {!isClRebateEnabled && !isSubscriptionLenses && (
                <PayOverTime location={PayOverTimeLocations.CART} />
            )}

            <div
                className={classNames(
                    {
                        [s.advantagesRebate]: isClRebateEnabled,
                    },
                    s.advantagesV2,
                )}
            >
                <Advantages analyticTracker="cart" />
            </div>

            <CartSummaryButtons
                disablePaymentButtons={isDisablePaymentButtons}
                goToCheckout={goToCheckout}
                hasOutOfStock={hasOutOfStock}
                isZeroTotal={total === 0}
                itemsCount={itemsCount}
                loadingButton={loadingButton}
            />
            {isDCartUICouponBox && (
                <div {...buttonProps} className={classNames(s.referer)} role="none">
                    <MentionMe
                        params={{
                            implementation: 'link',
                            locale: 'en_GB',
                            situation: 'dashboard',
                        }}
                        integratingType={REFERER_FIND}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    activeItemsCount: getCartItemsCount(store),
    copay: Number(store.cart.totals.copay),
    hasOutOfStock: getHasOutOfStock(store),
    isSubscriptionLenses: getIsCartHasActiveSubscriptionCL(store),
    itemsCount: store.cart.quoteItems.length,
    shippingProtection: store.cart.totals.shippingProtectionPrice || 0,
    subtotal: Number(store.cart.totals.subtotal),
    total: Number(store.cart.totals.total),
    totalRebateDiscount: getTotalRebateSum(store),
});

export const CartSummaryConnected = connect(mapStateToProps)(CartSummary);
